import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type HashValues = Record<string, string>;

type HashParams = {
    get(name: string): string | null;
    has(name: string): boolean;
    delete(name: string): void;

    set(name: string, value: string): void;
    set(values: HashValues, reset?: boolean): void;
};

export function useHashParams(): HashParams {
    const location = useLocation();
    const navigate = useNavigate();
    const hash = location.hash.slice(1);

    const hashParams = useMemo<HashParams>(() => {
        const currentParams = new URLSearchParams(hash);

        function set(name: string, value: string): void;
        function set(values: HashValues, reset?: boolean): void;

        function set(
            nameOrValues: string | HashValues,
            valueOrReset?: string | boolean
        ): void {
            /*
             * Si le premier paramètre est un objet et que reset=true,
             * on part d'un hash vide.
             */
            const shouldReset =
                typeof nameOrValues === "object" &&
                valueOrReset === true;

            const updatedParams = shouldReset
                ? new URLSearchParams()
                : new URLSearchParams(hash);

            if (typeof nameOrValues === "string") {
                updatedParams.set(
                    nameOrValues,
                    valueOrReset as string
                );
            } else {
                Object.entries(nameOrValues).forEach(([name, value]) => {
                    updatedParams.set(name, value);
                });
            }

            navigate({
                pathname: location.pathname,
                search: location.search,
                hash: updatedParams.toString(),
            });
        }

        function deleteParam(name: string): void {
            const updatedParams = new URLSearchParams(hash);

            updatedParams.delete(name);
            navigate({
                pathname: location.pathname,
                search: location.search,
                hash: updatedParams.toString(),
            });
        }

        return {
            get: (name: string) => currentParams.get(name),
            has: (name: string) => currentParams.has(name),
            delete: deleteParam,
            set,
        };
    }, [hash, location.pathname, location.search, navigate]);

    return hashParams;
}
